import { useCallback, useRef, useState } from "react";
import { gate } from "@/lib/rules";
import { parseCiPlWorkbook, type ParseReport } from "@/lib/parse";
import type { GateResult } from "@/types";
import { Disclaimer } from "@/components/Disclaimer";
import { FindingsTable, GateSummary } from "@/components/FindingsTable";

/**
 * Entrance ① — Excel upload → validation (DB-free, client-side).
 *
 * Drop/select a CI+PL workbook → parse with @/lib/parse → run the rule engine
 * `gate` on the recovered context → show findings + a field-read report.
 *
 * HONEST banner: the upload check sees only what is INSIDE the document
 * (incoterm format, CI↔PL line consistency, currency, FOB+air…). Regulatory
 * checks that need data the document does not carry (IOR / FDA Initial Importer)
 * belong to the "딜 입력" screen.
 */
export function UploadValidatePage(): JSX.Element {
  const [fileName, setFileName] = useState<string | null>(null);
  const [result, setResult] = useState<GateResult | null>(null);
  const [report, setReport] = useState<ParseReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback(async (file: File) => {
    setBusy(true);
    setError(null);
    setResult(null);
    setReport(null);
    setFileName(file.name);
    try {
      const buf = await file.arrayBuffer();
      const { ctx, report: rep } = await parseCiPlWorkbook(buf);
      setReport(rep);
      setResult(gate(ctx));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, []);

  const onInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) void handleFile(f);
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) void handleFile(f);
  };

  return (
    <div>
      <Disclaimer />
      <section className="panel">
        <h2>엑셀 업로드 검증</h2>
        <p className="sub">
          작성 완료된 CI + PL 워크북(INVOICE-US + PACKING LIST 시트)을 올리면 룰
          엔진이 문서 내부 항목을 검사합니다. DB 불필요(클라이언트 처리).
        </p>

        <div className="notice-info" role="note">
          <strong>이 검증의 범위:</strong> 엑셀 업로드 검증은{" "}
          <strong>문서 안에 있는 항목만</strong> 봅니다 — 인코텀즈 형식, CI↔PL
          수량/중량/금액 정합, 통화, FOB+항공 등. <br />
          <strong>수입자(IOR / FDA Initial Importer) 지정</strong> 같은 규제
          검사는 엑셀에 정보가 없어 검사할 수 없습니다 —{" "}
          <strong>"딜 입력" 화면</strong>에서 확인하세요.
        </div>

        <div
          className={`dropzone${dragOver ? " over" : ""}`}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") inputRef.current?.click();
          }}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            onChange={onInput}
            style={{ display: "none" }}
          />
          {busy ? (
            <span>분석 중…</span>
          ) : fileName ? (
            <span>
              <strong>{fileName}</strong> — 다시 선택하거나 다른 파일을 드롭하세요.
            </span>
          ) : (
            <span>
              여기로 xlsx 파일을 드롭하거나 클릭해 선택하세요 (CI + PL 워크북)
            </span>
          )}
        </div>

        {error && (
          <div className="notice-error" role="alert">
            <strong>파싱 실패:</strong> {error}
          </div>
        )}

        {report && (
          <>
            <h3 className="section-title">읽어낸 항목</h3>
            <p className="sub">
              시트 인식: INVOICE-US{" "}
              <b>{report.sheetsFound.invoice ? "✓" : "✗"}</b> · PACKING LIST{" "}
              <b>{report.sheetsFound.packing ? "✓" : "✗"}</b> · 품목{" "}
              <b>{report.lineCount}</b>행
            </p>
            <table className="findings">
              <thead>
                <tr>
                  <th>필드</th>
                  <th>값</th>
                  <th>읽음</th>
                </tr>
              </thead>
              <tbody>
                {report.fields.map((f) => (
                  <tr key={f.label}>
                    <td>{f.label}</td>
                    <td>{f.value || <span className="muted">(비어 있음)</span>}</td>
                    <td>
                      {f.read ? (
                        <span className="badge INFO">읽음</span>
                      ) : (
                        <span className="muted">못 읽음</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {report.warnings.length > 0 && (
              <ul className="warn-list">
                {report.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            )}
          </>
        )}

        {result && (
          <>
            <h3 className="section-title">검증 결과</h3>
            <GateSummary
              blocks={result.blocks}
              errors={result.errors}
              warns={result.warns}
              infos={result.infos}
              passed={result.passed}
            />
            <FindingsTable
              findings={result.findings}
              emptyText="문서 내부 항목에서 발견된 이슈 없음."
            />
            <p className="sub" style={{ marginTop: 12 }}>
              참고: 위 결과는 <strong>엑셀 문서가 담고 있는 정보</strong>만으로
              평가한 것입니다. 미국 수입자 지정·규제 식별자 등 문서 밖 항목은
              "딜 입력" 화면에서 별도로 검증됩니다.
            </p>
          </>
        )}
      </section>
    </div>
  );
}
